import { useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import QueryStatsOutlinedIcon from '@mui/icons-material/QueryStatsOutlined'
import { client } from '../../hooks/Client'
import useSettings from '../../hooks/Settings'
import { useAddressStatusCountsState } from '../../hooks/useAddressStatusCounts'
import buildStatusChartData from '../../helpers/adressHelpers/buildStatusChartData'
import HouseVisitsStatusVisualization from '../dataVisualizations/HouseVisitsStatusVisualization'
import StaticCardLabel from './StaticCardLabel'
import DashboardCardOptions from './DashboardCardOptions'
import DashboardCardEmptyState from './DashboardCardEmptyState'
import { FILTER_EMPTY_STATE } from './dashboardEmptyStateConfig'
import { CATEGORICAL } from '../../helpers/chartPalette'
import useSharedCollection from '../../firebase/useSharedCollection'
import useDashboardCardSetting from '../../hooks/useDashboardCardSetting'
import { DASHBOARD_FILTER_FIELDS } from '../../helpers/dashboardHelpers/dashboardCardFilters'
import hasLocationFilter from '../../helpers/dashboardHelpers/hasLocationFilter'
import DashboardFilterBar from './DashboardFilterBar'
import DashboardLocationFilter from './DashboardLocationFilter'

// AI-CONTEXT: See docs/patterns.md#dashboard-static-card
// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// Dashboard wrapper around the shared HouseVisitsStatusVisualization. Owns
// the data hook + the deep-link click handler; the chart itself lives in
// the shared component so the live-report widget renders identically.

const DashboardHouseVisitsStatusCard = ({ item }) => {
    const { housevisits } = useSettings()
    // Locaties zijn één instelling, gedeeld met de deelnemers. De keuze wordt
    // per kaart bewaard op het DashboardItems-document, net als bij de
    // deelnemerkaarten. AI-CONTEXT: See docs/features/huishouden-locaties.md
    const locations = useSharedCollection('Locations')
    const [selectedLocationId, setSelectedLocationId] = useDashboardCardSetting(
        item,
        DASHBOARD_FILTER_FIELDS.SelectedLocationId,
        '',
    )
    const { loaded, counts: statusCounts } = useAddressStatusCountsState(selectedLocationId)
    const history = useHistory()
    // Vaste paletkleur. Deze kaart staat alleen op het dashboard, dus er valt
    // niets te kiezen; de opgeslagen ColorOverrides blijven in Firestore staan
    // maar worden niet meer gelezen. Zie docs/patterns.md#ui-chart-palette.
    const barColor = CATEGORICAL[0]

    const statusData = useMemo(() => buildStatusChartData(statusCounts), [statusCounts])

    const goToStatus = (payload) => {
        const statusId = payload?.statusId
        if (!statusId) return
        history.push(`/${client}/housevisitsoverview?status=${statusId}`)
    }

    if (housevisits !== true) return null

    const isEmpty = statusData.every((d) => d.value.total === 0)
    // 'Nog geen huishoudens' is een bewering over de hele organisatie; met een
    // filter aan gaat het over één locatie.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    const isFilterEmpty = isEmpty && !!selectedLocationId

    return (
        <div className='housevisits-card-body'>
            <StaticCardLabel
                title='Status + langst wachtende'
                pill={{ variant: 'housevisits', label: 'Huishoudens' }}
            />
            <DashboardFilterBar
                onClear={() => setSelectedLocationId('')}
                activeFilterCount={selectedLocationId ? 1 : 0}
                hasFilters={hasLocationFilter(locations)}
            >
                <DashboardLocationFilter
                    selectedLocationId={selectedLocationId}
                    onChange={setSelectedLocationId}
                />
            </DashboardFilterBar>
            <div className='housevisits-card-chart'>
                {!loaded ? (
                    // Nog niets te tellen: geen 'Nog geen huisbezoeken' zolang de huishoudens laden.
                    <p className='score-explain-card__placeholder'>Laden…</p>
                ) : isFilterEmpty ? (
                    <DashboardCardEmptyState
                        {...FILTER_EMPTY_STATE}
                        onCtaClick={() => setSelectedLocationId('')}
                    />
                ) : isEmpty ? (
                    <DashboardCardEmptyState type='housevisits-status' />
                ) : (
                    <HouseVisitsStatusVisualization
                        data={statusData}
                        color={barColor}
                        height={'100%'}
                        onItemClick={goToStatus}
                    />
                )}
            </div>
            {/* De kaart toont de stand van nu; per periode en per dienst staat
                op een eigen pagina, als icoon in de iconenbalk (zoals
                'Huishoudens inplannen' op de agendakaart).
                AI-CONTEXT: See docs/features/huishoudens-status-per-periode.md */}
            <DashboardCardOptions
                item={item}
                extraActions={[{
                    id: 'statusPerPeriode',
                    label: 'Per periode bekijken',
                    // Niet het kalendericoon: dat betekent op andere kaarten 'kies de
                    // periode van deze kaart' (CardPeriodControl). Testronde 2026-10-13, R1-B2.
                    Icon: QueryStatsOutlinedIcon,
                    onClick: () => history.push(`/${client}/housevisitsstatus`),
                }]}
            />
        </div>
    )
}

export default DashboardHouseVisitsStatusCard
